import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate, Link } from "@tanstack/react-router";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import {
  ArrowLeft, DollarSign, Users, Package, Plus, X, Trash2, Pencil, Percent, Activity, Search,
} from "lucide-react";

// ----- Types -----

interface Tier {
  id: number;
  name: string;
  level: number;
  status: string;
  description: string;
  payoutMargin: number;
  isDefaultTier: boolean;
  affiliates: Array<{ id: number; name: string; status: string }>;
}

interface TierPayout {
  id: number;
  tierId: number;
  entryName: string;
  payoutType: string;
  payoutAmount: number;
  payoutPercentage: number;
  revenueType: string;
  revenueAmount: number;
  revenuePercentage: number;
  isDynamicRevenue: boolean;
  isDefault: boolean;
}

interface OfferLink {
  id: number;
  offerId: number;
  offerName: string;
  offerStatus: string;
  createdAt: string;
}

// ----- Constants -----

const PAYOUT_TYPES = [
  { value: "cpa", label: "CPA", description: "Cost Per Action — fixed payout per conversion" },
  { value: "cpc", label: "CPC", description: "Cost Per Click — fixed payout per click" },
  { value: "cpm", label: "CPM", description: "Cost Per Mille — fixed payout per 1000 impressions" },
  { value: "cps", label: "CPS", description: "Cost Per Sale — fixed payout per sale" },
  { value: "cpi", label: "CPI", description: "Cost Per Install — fixed payout per install" },
  { value: "revshare", label: "RevShare", description: "Revenue Share — payout is a % of revenue" },
];

const REVENUE_TYPES = [
  { value: "rpa", label: "RPA", description: "Revenue Per Action" },
  { value: "rpc", label: "RPC", description: "Revenue Per Click" },
  { value: "rpm", label: "RPM", description: "Revenue Per Mille" },
  { value: "rps", label: "RPS", description: "Revenue Per Sale" },
  { value: "rpi", label: "RPI", description: "Revenue Per Install" },
];

function formatPayout(p: TierPayout): string {
  if (p.payoutType === "revshare") return `${p.payoutPercentage}% of revenue`;
  return `$${(p.payoutAmount ?? 0).toFixed(2)}`;
}

function formatRevenue(p: TierPayout): string {
  if (p.isDynamicRevenue) return "Dynamic (per conversion)";
  return `$${(p.revenueAmount ?? 0).toFixed(2)}`;
}

// ----- Main Component -----

export function TierDetailPage() {
  const { id } = useParams({ strict: false });
  const navigate = useNavigate();
  const [tab, setTab] = useState<"details" | "payouts" | "members" | "offers">("payouts");

  const { data, isLoading } = useQuery({
    queryKey: ["tiers", id],
    queryFn: async () => {
      const res = await api.get(`/v1/tiers/${id}`);
      if (!res.ok) throw new Error("Failed to fetch tier");
      return res.json();
    },
  });

  const tier: Tier | undefined = data?.data;

  if (isLoading) {
    return <div className="space-y-4"><div className="h-10 bg-gray-100 rounded animate-pulse" /><div className="h-64 bg-gray-100 rounded-xl animate-pulse" /></div>;
  }

  if (!tier) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Tier not found</p>
        <button onClick={() => navigate({ to: "/tiers" })} className="text-sm text-blue-600 mt-2 hover:underline">Back to tiers</button>
      </div>
    );
  }

  const tabs = [
    { key: "payouts", label: "Payouts", icon: DollarSign },
    { key: "members", label: "Members", icon: Users },
    { key: "offers", label: "Assigned Offers", icon: Package },
  ] as const;

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate({ to: "/tiers" })} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-gray-900">{tier.name}</h1>
            <StatusBadge status={tier.status} />
            {tier.isDefaultTier && (
              <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">Default Tier</span>
            )}
          </div>
          {tier.description && <p className="text-sm text-gray-500 mt-1">{tier.description}</p>}
        </div>
      </div>

      <div className="flex gap-1 border-b border-gray-200 mb-6">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.key
                ? "border-gray-900 text-gray-900"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      {tab === "payouts" && <TierPayoutsTab tierId={id!} />}
      {tab === "members" && <TierMembersTab tierId={id!} currentMembers={tier.affiliates} />}
      {tab === "offers" && <TierOffersTab tierId={id!} />}
    </div>
  );
}

// ----- Payouts Tab -----

function TierPayoutsTab({ tierId }: { tierId: string }) {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingPayout, setEditingPayout] = useState<TierPayout | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["tiers", tierId, "payouts"],
    queryFn: async () => {
      const res = await api.get(`/v1/tiers/${tierId}/payouts`);
      if (!res.ok) throw new Error("Failed to fetch");
      return res.json();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (payoutId: number) => {
      const res = await api.delete(`/v1/tiers/${tierId}/payouts/${payoutId}`);
      if (!res.ok) throw new Error("Failed to delete");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tiers", tierId, "payouts"] }),
  });

  const createMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const res = await api.post(`/v1/tiers/${tierId}/payouts`, payload);
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || "Failed"); }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tiers", tierId, "payouts"] });
      setShowModal(false);
    },
  });

  const payouts: TierPayout[] = data?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">
          Configure payout entries for this tier. The <strong>default</strong> entry will be used when this tier overrides an offer's payout.
        </p>
        <button onClick={() => setShowModal(true)} className="inline-flex items-center gap-2 bg-gray-900 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 shrink-0">
          <Plus className="w-4 h-4" /> Add Payout
        </button>
      </div>

      {isLoading ? (
        <div className="h-32 bg-gray-100 rounded-xl animate-pulse" />
      ) : payouts.length === 0 ? (
        <div className="text-center py-8 bg-white rounded-xl border border-gray-200">
          <DollarSign className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No payout entries yet.</p>
          <p className="text-xs text-gray-400 mt-1">Add a payout entry to define what affiliates in this tier earn.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {payouts.map((payout) => (
            <div key={payout.id} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <h4 className="text-sm font-semibold text-gray-900">{payout.entryName}</h4>
                    {payout.isDefault && (
                      <span className="inline-flex items-center rounded-full bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 text-xs font-medium">Default</span>
                    )}
                    {payout.payoutType === "revshare" && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-purple-50 text-purple-700 border border-purple-200 px-2 py-0.5 text-xs font-medium">
                        <Percent className="w-3 h-3" /> RevShare
                      </span>
                    )}
                    {payout.isDynamicRevenue && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 text-xs font-medium">
                        <Activity className="w-3 h-3" /> Dynamic Revenue
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Payout</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {PAYOUT_TYPES.find((t) => t.value === payout.payoutType)?.label || (payout.payoutType ?? "").toUpperCase()}{" "}
                        — {formatPayout(payout)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Revenue</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {REVENUE_TYPES.find((t) => t.value === payout.revenueType)?.label || (payout.revenueType ?? "").toUpperCase()}{" "}
                        — {formatRevenue(payout)}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 ml-3 shrink-0">
                  <button onClick={() => setEditingPayout(payout)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg">
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => { if (window.confirm(`Delete "${payout.entryName}"?`)) deleteMutation.mutate(payout.id); }}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <TierPayoutFormModal
          onClose={() => setShowModal(false)}
          onCreate={(p) => createMutation.mutate(p)}
          isLoading={createMutation.isPending}
        />
      )}

      {editingPayout && (
        <TierPayoutEditModal
          tierId={tierId}
          payout={editingPayout}
          onClose={() => setEditingPayout(null)}
        />
      )}
    </div>
  );
}

// ----- Payout Form Modal (Create) -----

function TierPayoutFormModal({ onClose, onCreate, isLoading }: { onClose: () => void; onCreate: (p: Record<string, unknown>) => void; isLoading: boolean }) {
  const [entryName, setEntryName] = useState("");
  const [payoutType, setPayoutType] = useState("cpa");
  const [payoutAmount, setPayoutAmount] = useState("");
  const [payoutPercentage, setPayoutPercentage] = useState("");
  const [revenueType, setRevenueType] = useState("rpa");
  const [revenueAmount, setRevenueAmount] = useState("");
  const [isDynamicRevenue, setIsDynamicRevenue] = useState(false);
  const [isDefault, setIsDefault] = useState(false);
  const [error, setError] = useState("");
  const isRevShare = payoutType === "revshare";

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (!entryName.trim()) { setError("Entry name is required"); return; }

    onCreate({
      entryName: entryName.trim(),
      payoutType,
      payoutAmount: isRevShare ? 0 : Number(payoutAmount),
      payoutPercentage: isRevShare ? Number(payoutPercentage) : 0,
      revenueType,
      revenueAmount: isDynamicRevenue ? 0 : Number(revenueAmount),
      isDynamicRevenue,
      isDefault,
    });
  }

  const inputCls = "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Add Tier Payout Entry</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg border border-red-200">{error}</div>}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Entry Name *</label>
            <input type="text" required value={entryName} onChange={(e) => setEntryName(e.target.value)} className={inputCls} placeholder="e.g., RevShare 100%" />
          </div>

          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-medium text-gray-900">Payout (what the affiliate earns)</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payout Type *</label>
                <select required value={payoutType} onChange={(e) => setPayoutType(e.target.value)} className={inputCls}>
                  {PAYOUT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <p className="text-xs text-gray-400 mt-1">{PAYOUT_TYPES.find((t) => t.value === payoutType)?.description}</p>
              </div>
              <div>
                {isRevShare ? (
                  <>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Revenue Share % *</label>
                    <div className="relative">
                      <input type="number" required min="0" max="100" step="0.1" value={payoutPercentage} onChange={(e) => setPayoutPercentage(e.target.value)} className={inputCls} placeholder="e.g., 100" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                    </div>
                  </>
                ) : (
                  <>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payout Amount *</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                      <input type="number" required min="0" step="0.01" value={payoutAmount} onChange={(e) => setPayoutAmount(e.target.value)} className={`${inputCls} pl-7`} placeholder="0.00" />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-900">Revenue (what the advertiser pays)</h3>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={isDynamicRevenue} onChange={(e) => setIsDynamicRevenue(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900" />
                <span className="text-xs font-medium text-amber-700">Dynamic revenue</span>
              </label>
            </div>
            {isDynamicRevenue && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <p className="text-xs text-amber-800">Revenue varies per conversion, passed via the postback parameters.</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Revenue Type *</label>
                <select required value={revenueType} onChange={(e) => setRevenueType(e.target.value)} className={inputCls}>
                  {REVENUE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              {!isDynamicRevenue && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Revenue Amount *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                    <input type="number" required min="0" step="0.01" value={revenueAmount} onChange={(e) => setRevenueAmount(e.target.value)} className={`${inputCls} pl-7`} placeholder="0.00" />
                  </div>
                </div>
              )}
            </div>
          </div>

          {isRevShare && isDynamicRevenue && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg px-4 py-3">
              <p className="text-xs text-purple-800 font-medium mb-1">RevShare + Dynamic Revenue</p>
              <p className="text-xs text-purple-700">
                Payout = <code className="bg-purple-100 px-1 rounded">revenue × {payoutPercentage || "??"}%</code>. Each conversion's payout varies with its revenue.
              </p>
            </div>
          )}

          <div className="flex items-center gap-2">
            <input type="checkbox" id="tierIsDefault" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900" />
            <label htmlFor="tierIsDefault" className="text-sm font-medium text-gray-700">Set as default payout entry</label>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={isLoading} className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50">
              {isLoading ? "Creating..." : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ----- Payout Edit Modal -----

function TierPayoutEditModal({ tierId, payout, onClose }: { tierId: string; payout: TierPayout; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [entryName, setEntryName] = useState(payout.entryName || "");
  const [payoutType, setPayoutType] = useState(payout.payoutType || "cpa");
  const [payoutAmount, setPayoutAmount] = useState(String(payout.payoutAmount ?? 0));
  const [payoutPercentage, setPayoutPercentage] = useState(String(payout.payoutPercentage ?? 0));
  const [revenueType, setRevenueType] = useState(payout.revenueType || "rpa");
  const [revenueAmount, setRevenueAmount] = useState(String(payout.revenueAmount ?? 0));
  const [isDynamicRevenue, setIsDynamicRevenue] = useState(payout.isDynamicRevenue || false);
  const [isDefault, setIsDefault] = useState(payout.isDefault || false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const isRevShare = payoutType === "revshare";

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api.patch(`/v1/tiers/${tierId}/payouts/${payout.id}`, {
        entryName: entryName.trim(),
        payoutType,
        payoutAmount: isRevShare ? 0 : Number(payoutAmount),
        payoutPercentage: isRevShare ? Number(payoutPercentage) : 0,
        revenueType,
        revenueAmount: isDynamicRevenue ? 0 : Number(revenueAmount),
        isDynamicRevenue,
        isDefault,
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || "Failed"); }
      queryClient.invalidateQueries({ queryKey: ["tiers", tierId, "payouts"] });
      onClose();
    } catch (err: any) { setError(err.message); } finally { setLoading(false); }
  }

  const inputCls = "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Edit Payout Entry</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg border border-red-200">{error}</div>}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Entry Name *</label>
            <input type="text" required value={entryName} onChange={(e) => setEntryName(e.target.value)} className={inputCls} />
          </div>

          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-medium text-gray-900">Payout</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payout Type</label>
                <select value={payoutType} onChange={(e) => setPayoutType(e.target.value)} className={inputCls}>
                  {PAYOUT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                {isRevShare ? (
                  <>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Revenue Share %</label>
                    <input type="number" min="0" max="100" step="0.1" value={payoutPercentage} onChange={(e) => setPayoutPercentage(e.target.value)} className={inputCls} />
                  </>
                ) : (
                  <>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payout Amount</label>
                    <input type="number" min="0" step="0.01" value={payoutAmount} onChange={(e) => setPayoutAmount(e.target.value)} className={inputCls} />
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-900">Revenue</h3>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={isDynamicRevenue} onChange={(e) => setIsDynamicRevenue(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900" />
                <span className="text-xs font-medium text-amber-700">Dynamic</span>
              </label>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Revenue Type</label>
                <select value={revenueType} onChange={(e) => setRevenueType(e.target.value)} className={inputCls}>
                  {REVENUE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              {!isDynamicRevenue && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Revenue Amount</label>
                  <input type="number" min="0" step="0.01" value={revenueAmount} onChange={(e) => setRevenueAmount(e.target.value)} className={inputCls} />
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input type="checkbox" id="editTierIsDefault" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900" />
            <label htmlFor="editTierIsDefault" className="text-sm font-medium text-gray-700">Set as default</label>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={loading} className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50">
              {loading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ----- Members Tab -----

function TierMembersTab({ tierId, currentMembers }: { tierId: string; currentMembers: Array<{ id: number; name: string; status: string }> }) {
  const queryClient = useQueryClient();
  const [showPicker, setShowPicker] = useState(false);
  const [search, setSearch] = useState("");

  const { data: tierData } = useQuery({
    queryKey: ["tiers", tierId],
    queryFn: async () => {
      const res = await api.get(`/v1/tiers/${tierId}`);
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const members: Array<{ id: number; name: string; status: string }> = tierData?.data?.affiliates ?? currentMembers;

  const removeMutation = useMutation({
    mutationFn: async (affiliateId: number) => {
      const res = await api.delete(`/v1/tiers/${tierId}/affiliates`, { affiliateIds: [affiliateId] });
      if (!res.ok) throw new Error("Failed");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tiers", tierId] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">{members.length} affiliate{members.length !== 1 ? "s" : ""} in this tier</p>
        <button onClick={() => setShowPicker(true)} className="inline-flex items-center gap-2 bg-gray-900 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-800">
          <Plus className="w-4 h-4" /> Add Affiliates
        </button>
      </div>

      {members.length === 0 ? (
        <div className="text-center py-8 bg-white rounded-xl border border-gray-200">
          <Users className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No affiliates in this tier yet.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">ID</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Name</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Status</th>
                <th className="text-right text-xs font-medium text-gray-500 uppercase px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-500">{m.id}</td>
                  <td className="px-4 py-3">
                    <Link to={`/affiliates/${m.id}` as any} className="text-sm font-medium text-blue-600 hover:underline">{m.name}</Link>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={m.status} /></td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => { if (window.confirm(`Remove "${m.name}" from this tier?`)) removeMutation.mutate(m.id); }}
                      className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showPicker && (
        <AffiliatePickerModal
          tierId={tierId}
          existingIds={members.map((m) => m.id)}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
}

// ----- Affiliate Picker Modal -----

function AffiliatePickerModal({ tierId, existingIds, onClose }: { tierId: string; existingIds: number[]; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);

  const { data } = useQuery({
    queryKey: ["affiliates", { search, pageSize: 100 }],
    queryFn: async () => {
      const qp: Record<string, string> = { pageSize: "100" };
      if (search) qp.search = search;
      const res = await api.get("/v1/affiliates", qp);
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const affiliates: Array<{ id: number; name: string; status: string }> = (data?.data ?? []).filter(
    (a: any) => !existingIds.includes(a.id)
  );

  function toggleSelect(id: number) {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }

  async function handleAdd() {
    if (selectedIds.length === 0) return;
    setLoading(true);
    try {
      const res = await api.post(`/v1/tiers/${tierId}/affiliates`, { affiliateIds: selectedIds });
      if (!res.ok) throw new Error("Failed");
      queryClient.invalidateQueries({ queryKey: ["tiers", tierId] });
      onClose();
    } catch { } finally { setLoading(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-lg p-6 max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Add Affiliates to Tier</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1"><X className="w-5 h-5" /></button>
        </div>
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" placeholder="Search affiliates..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900" />
        </div>
        <div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
          {affiliates.length === 0 ? (
            <div className="text-center py-6 text-sm text-gray-400">No affiliates available</div>
          ) : affiliates.map((a) => (
            <label key={a.id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer">
              <input type="checkbox" checked={selectedIds.includes(a.id)} onChange={() => toggleSelect(a.id)}
                className="w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900" />
              <span className="text-sm text-gray-900 flex-1">{a.name}</span>
              <StatusBadge status={a.status} />
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-3 mt-4">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={handleAdd} disabled={loading || selectedIds.length === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50">
            {loading ? "Adding..." : `Add ${selectedIds.length} Affiliate${selectedIds.length !== 1 ? "s" : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ----- Offers Tab -----

function TierOffersTab({ tierId }: { tierId: string }) {
  const queryClient = useQueryClient();
  const [showPicker, setShowPicker] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["tiers", tierId, "offers"],
    queryFn: async () => {
      const res = await api.get(`/v1/tiers/${tierId}/offers`);
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const offerLinks: OfferLink[] = data?.data ?? [];

  const removeMutation = useMutation({
    mutationFn: async (offerId: number) => {
      const res = await api.delete(`/v1/tiers/${tierId}/offers`, { offerIds: [offerId] });
      if (!res.ok) throw new Error("Failed");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tiers", tierId, "offers"] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">
          Offers where this tier's payout overrides the default.
          {offerLinks.length > 0 ? ` ${offerLinks.length} offer${offerLinks.length !== 1 ? "s" : ""} assigned.` : ""}
        </p>
        <button onClick={() => setShowPicker(true)} className="inline-flex items-center gap-2 bg-gray-900 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-800">
          <Plus className="w-4 h-4" /> Assign Offers
        </button>
      </div>

      {isLoading ? (
        <div className="h-32 bg-gray-100 rounded-xl animate-pulse" />
      ) : offerLinks.length === 0 ? (
        <div className="text-center py-8 bg-white rounded-xl border border-gray-200">
          <Package className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No offers assigned yet.</p>
          <p className="text-xs text-gray-400 mt-1">Assign offers so this tier's payout overrides their defaults.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Offer ID</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Offer Name</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Status</th>
                <th className="text-right text-xs font-medium text-gray-500 uppercase px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {offerLinks.map((link) => (
                <tr key={link.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-500">{link.offerId}</td>
                  <td className="px-4 py-3">
                    <Link to={`/offers/${link.offerId}` as any} className="text-sm font-medium text-blue-600 hover:underline">{link.offerName}</Link>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={link.offerStatus} /></td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => { if (window.confirm(`Remove this offer from the tier?`)) removeMutation.mutate(link.offerId); }}
                      className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showPicker && (
        <OfferPickerModal
          tierId={tierId}
          existingOfferIds={offerLinks.map((l) => l.offerId)}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
}

// ----- Offer Picker Modal -----

function OfferPickerModal({ tierId, existingOfferIds, onClose }: { tierId: string; existingOfferIds: number[]; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);

  const { data } = useQuery({
    queryKey: ["offers", { search, pageSize: 100 }],
    queryFn: async () => {
      const qp: Record<string, string> = { pageSize: "100" };
      if (search) qp.search = search;
      const res = await api.get("/v1/offers", qp);
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const offers: Array<{ id: number; name: string; status: string }> = (data?.data ?? []).filter(
    (o: any) => !existingOfferIds.includes(o.id)
  );

  function toggleSelect(id: number) {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }

  async function handleAssign() {
    if (selectedIds.length === 0) return;
    setLoading(true);
    try {
      const res = await api.post(`/v1/tiers/${tierId}/offers`, { offerIds: selectedIds });
      if (!res.ok) throw new Error("Failed");
      queryClient.invalidateQueries({ queryKey: ["tiers", tierId, "offers"] });
      onClose();
    } catch { } finally { setLoading(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-lg p-6 max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Assign Offers to Tier</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1"><X className="w-5 h-5" /></button>
        </div>
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" placeholder="Search offers..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900" />
        </div>
        <div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
          {offers.length === 0 ? (
            <div className="text-center py-6 text-sm text-gray-400">No offers available</div>
          ) : offers.map((o) => (
            <label key={o.id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer">
              <input type="checkbox" checked={selectedIds.includes(o.id)} onChange={() => toggleSelect(o.id)}
                className="w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900" />
              <span className="text-sm text-gray-900 flex-1">{o.name}</span>
              <StatusBadge status={o.status} />
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-3 mt-4">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={handleAssign} disabled={loading || selectedIds.length === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50">
            {loading ? "Assigning..." : `Assign ${selectedIds.length} Offer${selectedIds.length !== 1 ? "s" : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}
